import InfoIcon from "@mui/icons-material/Info";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ChartsColorPalette } from "@mui/x-charts";
import { BarChart } from "@mui/x-charts/BarChart";
import { DatasetElementType } from "@mui/x-charts/internals";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import * as React from "react";
import { axiosCacheApi } from "../../../interceptors/axios";

// TypeScript için interface tanımlamaları
type StatisticData = {
  creationTime: string;
  isGraduationVerified: boolean;
  emailConfirmed: boolean;
}[];

interface MonthlyData {
  month: string;
  totalCount: number;
  emailConfirmedCount: number;
  graduationVerifiedCount: number;
  growth: number;
  label: string;
}

// Tarih verilerini aylık olarak işleyen yardımcı fonksiyon
function processMonthlyData(data: StatisticData): MonthlyData[] {
  // Tarihleri dayjs nesnelerine dönüştürme
  const items = data.map((item) => ({
    date: dayjs(item.creationTime),
    emailConfirmed: item.emailConfirmed,
    isGraduationVerified: item.isGraduationVerified,
  }));

  // Tarihleri aylara göre gruplandırma
  const dataByMonth: Record<
    string,
    {
      total: number;
      emailConfirmed: number;
      graduationVerified: number;
    }
  > = {};

  items.forEach((item) => {
    const monthKey = item.date.format("YYYY-MM");

    if (!dataByMonth[monthKey]) {
      dataByMonth[monthKey] = {
        total: 0,
        emailConfirmed: 0,
        graduationVerified: 0,
      };
    }

    dataByMonth[monthKey].total += 1;

    if (item.emailConfirmed) {
      dataByMonth[monthKey].emailConfirmed += 1;
    }

    if (item.isGraduationVerified) {
      dataByMonth[monthKey].graduationVerified += 1;
    }
  });

  // Son 12 ay için veri oluştur
  const today = dayjs();
  const monthlyData: MonthlyData[] = [];

  for (let i = 11; i >= 0; i--) {
    const currentMonth = today.subtract(i, "month");
    const monthKey = currentMonth.format("YYYY-MM");
    const previousMonthKey = currentMonth
      .subtract(1, "month")
      .format("YYYY-MM");

    const currentData = dataByMonth[monthKey] || {
      total: 0,
      emailConfirmed: 0,
      graduationVerified: 0,
    };
    const previousData = dataByMonth[previousMonthKey] || {
      total: 0,
      emailConfirmed: 0,
      graduationVerified: 0,
    };

    // Büyüme yüzdesini hesapla (toplam kayıtlar için)
    let growth = 0;
    if (previousData.total > 0) {
      growth = Math.round(
        ((currentData.total - previousData.total) / previousData.total) * 100
      );
    } else if (currentData.total > 0) {
      growth = 100; // Önceki ay sıfırsa ve bu ay veri varsa, %100 artış
    }

    monthlyData.push({
      month: monthKey,
      totalCount: currentData.total,
      emailConfirmedCount: currentData.emailConfirmed,
      graduationVerifiedCount: currentData.graduationVerified,
      growth,
      label: currentMonth.format("MMM YYYY"),
    });
  }

  return monthlyData;
}

export default function MonthlyGrowthChart(): JSX.Element {
  const theme = useTheme();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [monthlyData, setMonthlyData] = React.useState<MonthlyData[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [showLegend, setShowLegend] = React.useState<boolean>(true);

  // API'den veri çekme
  React.useEffect(() => {
    const fetchStatistics = async (): Promise<void> => {
      setLoading(true);
      try {
        const response = await axiosCacheApi.get<StatisticData>(
          "api/users/statistic"
        );

        // Tarih verilerini aylık olarak işle
        const processed = processMonthlyData(response.data);
        setMonthlyData(processed);

        setLoading(false);
      } catch (err) {
        console.error("İstatistik verileri alınırken hata:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Veriler yüklenirken bir hata oluştu"
        );
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  // Yükleme durumu
  if (loading) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography>Veriler yükleniyor...</Typography>
        </CardContent>
      </Card>
    );
  }

  // Hata durumu
  if (error) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography color="error">Hata: {error}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Son 6 ay gösterilecek şekilde filtrele
  const last6Months = monthlyData.slice(-6);

  // Veri serilerini oluştur
  const chartData = {
    months: last6Months.map((item) => item.label),
    totalCounts: last6Months.map((item) => item.totalCount),
    emailConfirmedCounts: last6Months.map((item) => item.emailConfirmedCount),
    graduationVerifiedCounts: last6Months.map(
      (item) => item.graduationVerifiedCount
    ),
    growths: last6Months.map((item) => item.growth),
  };

  // Her ay için yan yana sütunlar için dataset oluştur
  const barChartData: DatasetElementType<
    string | number | Date | null | undefined
  >[] = chartData.months.map((month, index) => ({
    month,
    "Tüm Kayıtlar": chartData.totalCounts[index],
    "E-posta Onaylı": chartData.emailConfirmedCounts[index],
    "Mezuniyet Doğrulamalı": chartData.graduationVerifiedCounts[index],
    growth: chartData.growths[index],
  }));

  // Renk tanımları
  const barColors: ChartsColorPalette = [
    theme.palette.primary.main, // Tüm kayıtlar
    theme.palette.success.main, // E-posta onaylı
    theme.palette.info.main, // Mezuniyet doğrulamalı
  ];
  const colorPalate: ChartsColorPalette = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.info.main,
  ];
  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography component="h2" variant="subtitle2">
            Aylık Kayıt Dağılımı
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Değişim bilgisi göstergesini aç/kapat">
              <IconButton
                size="small"
                onClick={() => setShowLegend(!showLegend)}
                sx={{ mr: 1 }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {showLegend && (
          <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: barColors[0],
                  mr: 0.5,
                }}
              />
              <Typography variant="caption">Tüm Kayıtlar</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: barColors[1],
                  mr: 0.5,
                }}
              />
              <Typography variant="caption">E-posta Onaylı</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: barColors[2],
                  mr: 0.5,
                }}
              />
              <Typography variant="caption">Mezuniyet Doğrulamalı</Typography>
            </Box>
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Son 6 ayın kayıt dağılımları
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                borderRadius: 1,
              }}
            >
              <BarChart
                dataset={barChartData}
                xAxis={[
                  {
                    scaleType: "band",
                    dataKey: "month",
                    tickLabelStyle: {
                      fontSize: 12,
                    },
                  },
                ]}
                series={[
                  {
                    dataKey: "Tüm Kayıtlar",
                    label: "Tüm Kayıtlar",
                    valueFormatter: (value) => value?.toString() || "",
                    color: barColors[0],
                  },
                  {
                    dataKey: "E-posta Onaylı",
                    label: "E-posta Onaylı",
                    valueFormatter: (value) => value?.toString() || "",
                    color: barColors[1],
                  },
                  {
                    dataKey: "Mezuniyet Doğrulamalı",
                    label: "Mezuniyet Doğrulamalı",
                    valueFormatter: (value) => value?.toString() || "",
                    color: barColors[2],
                  },
                ]}
                hideLegend={!showLegend}
                height={300}
                margin={{ left: 40, right: 30, top: 40, bottom: 30 }}
                slotProps={{
                  legend: {
                    position: {
                      vertical: "top",
                      horizontal: "end",
                    },
                  },
                }}
              />
            </Box>
          </Grid>

          {/* Büyüme/Azalış bilgisi */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 2,
                mt: 1,
              }}
            >
              {last6Months.map((item, index) => (
                <Tooltip
                  key={index}
                  title={`Önceki aya göre değişim: ${
                    item.growth >= 0 ? "+" : ""
                  }${item.growth}%`}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid",
                      borderColor:
                        item.growth >= 0
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mr: 0.5 }}
                    >
                      {item.label}:
                    </Typography>
                    <Typography
                      variant="caption"
                      color={item.growth >= 0 ? "success.main" : "error.main"}
                      fontWeight="bold"
                    >
                      {item.growth >= 0 ? "+" : ""}
                      {item.growth}%
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
